import styled, { css } from 'styled-components';
import React from 'react';
import PropTypes from 'prop-types';
import { PageSection, SectionBody } from './PageSection';
import { Heading, Paragraph, Box } from 'grommet';
import { ButtonContainer, Button } from './Button';
import RichText from './RichText';
import { GatsbyImage } from 'gatsby-plugin-image';
import { DecoratedImage } from './DecoratedImage';
// Image

// DOWNLOAD BLOCK

const StyledDownloadBlock = styled.div`
  border: solid 3px transparent;
  border-color: ${({ theme }) => theme.global.colors.brand};
  border-left: none;
  border-right: none;
  padding: 2rem 0;
`;

export const DownloadBlock = ({ title, subtitle, ctaLabel, ctaLink, file, textColor }) => {
  return (
    <StyledDownloadBlock>
      <div>
        <Box pad={{ bottom: '1rem' }}>
          <Heading color={textColor} level="5">
            {title}
          </Heading>
          { subtitle !== null ? <Paragraph size='small'><em>{subtitle}</em></Paragraph> : null }          
        </Box>
        <ButtonContainer>
          <Button
            label={ctaLabel}
            href={ctaLink}
            target={'_blank'}
          />
        </ButtonContainer>
      </div>
    </StyledDownloadBlock>
  );
};

// DOWNLOAD SECTION BODY

const StyledBodyContent = styled.div``;

const NoImageLayout = css`
  display: grid;
  grid-gap: calc(var(--gutters) * 2);
  align-items: center;
  grid-template-columns: auto;
  grid-auto-rows: auto;

  @media ${({ theme }) => theme.custom.layouts.above_medium} {
    grid-gap: var(--gutters);
    justify-content: space-between;
    grid-template-rows: auto;
    grid-template-columns: calc(6 / 12 * 100%) calc(5 / 12 * 100%);
  }
`;

const WithImageLayout = css`
  /* Inherit the overall layout */
  ${NoImageLayout}

  & > div:nth-of-type(1) {
    grid-area: content;
  }
  & > div:nth-of-type(2) {
    grid-area: download;
  }
  & > div:nth-of-type(3) {
    grid-area: image;
  }

  grid-template-areas: 'image' 'content' 'download';

  @media ${({ theme }) => theme.custom.layouts.above_medium} {
    align-items: start;
    grid-template-areas: 'image content' 'image download';
    grid-gap: var(--gutters);
    justify-content: space-between;
    grid-template-rows: auto;
    grid-template-columns: calc(4 / 12 * 100%) calc(7 / 12 * 100%);
  }
  @media ${({ theme }) => theme.custom.layouts.above_medium} {
    grid-template-columns: calc(5 / 12 * 100%) calc(6 / 12 * 100%);
  }
`;

const StyledBodyGrid = styled.div`
  ${({ hasImage }) => {
    if (hasImage) return WithImageLayout;
    return NoImageLayout;
  }}
`;

const DownloadSection = ({
  title,
  body,
  ctaLabel,
  ctaLink,
  image,
  downloadTitle,
  downloadSubtitle,
  file,
  theme,
}) => {
  const hasImage = image !== null;
  const isDarkTheme = theme === 'dark';

  return (
    <PageSection backgroundColor={isDarkTheme ? 'dark' : 'white'}>
      <SectionBody>
        <StyledBodyGrid hasImage={hasImage}>
          {/* Body Content */}
          <StyledBodyContent>
            <Box pad={{ bottom: '1rem' }}>
              <Heading color={isDarkTheme ? 'light-1' : null} level="2">
                {title}
              </Heading>
            </Box>
            <RichText
              textColor={isDarkTheme ? 'white' : 'black'}
              richText={body}
            />
          </StyledBodyContent>
          {/* Download Block*/}
          <DownloadBlock
            textColor={isDarkTheme ? 'white' : 'black'}
            title={downloadTitle}
            subtitle={downloadSubtitle}
            ctaLink={ctaLink}
            // file={file}
            ctaLabel={ctaLabel}
          />
          {/* Image */}
          {hasImage ? (
            <Box>
              <DecoratedImage>
                <GatsbyImage image={image.gatsbyImageData} />
              </DecoratedImage>
            </Box>
          ) : null}
        </StyledBodyGrid>
      </SectionBody>
    </PageSection>
  );
};

DownloadSection.defaultProps = {
  image: null,
  file: null,
  theme: 'light',
};

DownloadSection.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.object.isRequired,
  ctaLabel: PropTypes.string.isRequired,
  theme: PropTypes.string,
  image: PropTypes.object,
  file: PropTypes.object.isRequired,
  ctaLink: PropTypes.string.isRequired,
};

export default DownloadSection;
